"use client";

import { useMemo } from "react";
import { Filter, RotateCcw } from "lucide-react";
import { Button, Checkbox, Input, Label } from "@eg/ui";
import type {
  CategoryId,
  DiscoveryFilters,
  FacetOverview,
} from "@/lib/discovery";
import { useLocale, useTranslations } from "@/lib/i18n/I18nProvider";

type Props = {
  filters: DiscoveryFilters;
  facets: FacetOverview;
  onChange: (next: Partial<DiscoveryFilters>) => void;
  onReset: () => void;
  onClose?: () => void;
};

const preferredBadgeOrder = ["new", "sale", "shipping"] as const;

function formatPriceRange(locale: string, min?: number, max?: number) {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
  return `${formatter.format((min ?? 0) / 100)} – ${formatter.format((max ?? 0) / 100)}`;
}

export function FilterSidebar({
  filters,
  facets,
  onChange,
  onReset,
  onClose,
}: Props) {
  const locale = useLocale();
  const t = useTranslations();
  const categoryLabel = useMemo(() => {
    const map: Record<CategoryId, string> = {
      vaporizer: t("categories.vaporizer"),
      grinder: t("categories.grinder"),
      papers: t("categories.papers"),
      filterTips: t("categories.filterTips"),
      cones: t("categories.cones"),
      lighters: t("categories.lighters"),
      storage: t("categories.storage"),
      accessories: t("categories.accessories"),
      cleaning: t("categories.cleaning"),
      scales: t("categories.scales"),
      humidor: t("categories.humidor"),
      assortment: t("categories.assortment"),
    };
    return (id: CategoryId) => map[id] ?? id;
  }, [t]);

  const badgeLabel = useMemo(
    () => ({
      sale: t("discovery.product.badges.sale"),
      new: t("discovery.product.badges.new"),
      shipping: t("discovery.product.badges.shipping"),
    }),
    [t],
  );

  const categoryOptions = useMemo(
    () =>
      Object.entries(facets.categories)
        .map(([id, count]) => ({
          id: id as CategoryId,
          count,
          label: categoryLabel(id as CategoryId),
        }))
        .filter((item) => item.count > 0)
        .sort(
          (a, b) => b.count - a.count || a.label.localeCompare(b.label, locale),
        ),
    [categoryLabel, facets.categories, locale],
  );

  const brandOptions = useMemo(
    () =>
      Object.entries(facets.brands)
        .filter(([, count]) => count > 0)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], locale))
        .map(([name, count]) => ({ name, count })),
    [facets.brands, locale],
  );

  const badgeOptions = useMemo(
    () =>
      Object.entries(facets.badges)
        .filter(([, count]) => count > 0)
        .sort(([a], [b]) => {
          const rankA = preferredBadgeOrder.indexOf(
            a as (typeof preferredBadgeOrder)[number],
          );
          const rankB = preferredBadgeOrder.indexOf(
            b as (typeof preferredBadgeOrder)[number],
          );
          const safeRankA = rankA === -1 ? Number.MAX_SAFE_INTEGER : rankA;
          const safeRankB = rankB === -1 ? Number.MAX_SAFE_INTEGER : rankB;
          if (safeRankA !== safeRankB) return safeRankA - safeRankB;
          const labelA = badgeLabel[a as keyof typeof badgeLabel] ?? a;
          const labelB = badgeLabel[b as keyof typeof badgeLabel] ?? b;
          return labelA.localeCompare(labelB, locale);
        })
        .map(([id, count]) => ({
          id,
          count,
          label: badgeLabel[id as keyof typeof badgeLabel] ?? id,
        })),
    [badgeLabel, facets.badges, locale],
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.categories.length) count += 1;
    if (filters.brands.length) count += 1;
    if (filters.badges.length) count += 1;
    if (filters.availability === "in_stock") count += 1;
    if (
      typeof filters.priceMin === "number" ||
      typeof filters.priceMax === "number"
    ) {
      count += 1;
    }
    return count;
  }, [
    filters.availability,
    filters.badges.length,
    filters.brands.length,
    filters.categories.length,
    filters.priceMax,
    filters.priceMin,
  ]);

  const priceLabel = useMemo(
    () =>
      formatPriceRange(
        locale,
        filters.priceMin ?? facets.priceRange.min,
        filters.priceMax ?? facets.priceRange.max,
      ),
    [
      facets.priceRange.max,
      facets.priceRange.min,
      locale,
      filters.priceMax,
      filters.priceMin,
    ],
  );

  const minPlaceholder = Math.round((facets.priceRange.min ?? 0) / 100);
  const maxPlaceholder = Math.round((facets.priceRange.max ?? 0) / 100);

  return (
    <aside className="flex w-full max-w-sm flex-col gap-4 rounded-3xl border border-slate-200 bg-slate-50/95 p-4 shadow-sm backdrop-blur md:sticky md:top-28 md:h-[calc(100vh-8rem)] md:overflow-auto">
      <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-700">
              <Filter className="h-4 w-4" />
              {t("discovery.filterSidebar.title")}
            </div>
          </div>
          <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-cyan-300 bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-800">
            {activeFilterCount}
          </span>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="border border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
          >
            <RotateCcw className="h-4 w-4" />
            {t("common.reset")}
          </Button>
          {onClose ? (
            <Button
              variant="outline"
              size="sm"
              className="border-slate-300 text-slate-800 hover:bg-slate-100"
              onClick={onClose}
            >
              {t("common.close")}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
        <Label className="text-xs uppercase tracking-wide text-slate-600">
          {t("discovery.filterSidebar.availability")}
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            {
              label: t("discovery.filterSidebar.availabilityAll"),
              value: "all",
            },
            {
              label: t("discovery.filterSidebar.availabilityInStock"),
              value: "in_stock",
            },
          ].map((item) => {
            const active = filters.availability === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() =>
                  onChange({
                    availability:
                      item.value as DiscoveryFilters["availability"],
                  })
                }
                className={`rounded-xl border px-3 py-2 text-left text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
                  active
                    ? "border-cyan-400 bg-cyan-50 text-cyan-900"
                    : "border-slate-300 bg-white text-slate-700 hover:border-cyan-300"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <Label className="text-xs uppercase tracking-wide text-slate-600">
          {t("discovery.filterSidebar.badges")}
        </Label>
        <div className="grid grid-cols-1 gap-2">
          {badgeOptions.map((badge) => {
            const active = filters.badges.includes(badge.id);
            return (
              <button
                key={badge.id}
                type="button"
                className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 ${
                  active
                    ? "border-cyan-400 bg-cyan-50 text-slate-900"
                    : "border-slate-300 bg-white text-slate-800 hover:border-cyan-300"
                }`}
                onClick={() => {
                  const next = new Set(filters.badges);
                  if (next.has(badge.id)) next.delete(badge.id);
                  else next.add(badge.id);
                  onChange({ badges: Array.from(next) });
                }}
              >
                <span className="truncate text-left">{badge.label}</span>
                <span className="text-xs text-slate-500">{badge.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
        <Label className="text-xs uppercase tracking-wide text-slate-600">
          {t("discovery.filterSidebar.categories")}
        </Label>
        <div className="max-h-56 space-y-2 overflow-auto pr-1">
          {categoryOptions.map((category) => {
            const active = filters.categories.includes(category.id);
            return (
              <label
                key={category.id}
                className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm transition ${
                  active
                    ? "border-cyan-300 bg-cyan-50/70"
                    : "border-slate-200 bg-white hover:border-cyan-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    className="border-slate-400 data-[state=checked]:border-cyan-600 data-[state=checked]:bg-cyan-600"
                    checked={active}
                    onCheckedChange={(checked) => {
                      const next = new Set(filters.categories);
                      if (checked === true) next.add(category.id);
                      else next.delete(category.id);
                      onChange({ categories: Array.from(next) });
                    }}
                  />
                  <span className="text-slate-800">{category.label}</span>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {category.count}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
        <Label className="text-xs uppercase tracking-wide text-slate-600">
          {t("discovery.filterSidebar.brands")}
        </Label>
        <div className="max-h-56 space-y-2 overflow-auto pr-1">
          {brandOptions.map((brand) => {
            const active = filters.brands.includes(brand.name);
            return (
              <button
                key={brand.name}
                type="button"
                className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 ${
                  active
                    ? "border-cyan-400 bg-cyan-50 text-slate-900"
                    : "border-slate-200 bg-white text-slate-800 hover:border-cyan-300"
                }`}
                onClick={() => {
                  const next = new Set(filters.brands);
                  if (next.has(brand.name)) next.delete(brand.name);
                  else next.add(brand.name);
                  onChange({ brands: Array.from(next) });
                }}
              >
                <span className="truncate">{brand.name}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {brand.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
        <Label className="text-xs uppercase tracking-wide text-slate-600">
          {t("discovery.filterSidebar.priceRange")}
        </Label>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <Input
            type="number"
            min={0}
            value={
              typeof filters.priceMin === "number"
                ? Math.round(filters.priceMin / 100)
                : ""
            }
            onChange={(e) => {
              const raw = e.target.value.trim();
              if (!raw.length) {
                onChange({ priceMin: undefined });
                return;
              }
              const next = Math.round(Math.max(0, Number(raw)) * 100);
              onChange({ priceMin: Number.isFinite(next) ? next : undefined });
            }}
            className="w-full border-slate-300 bg-white text-slate-900"
            placeholder={`${minPlaceholder}`}
          />
          <span className="text-slate-500">–</span>
          <Input
            type="number"
            min={0}
            value={
              typeof filters.priceMax === "number"
                ? Math.round(filters.priceMax / 100)
                : ""
            }
            onChange={(e) => {
              const raw = e.target.value.trim();
              if (!raw.length) {
                onChange({ priceMax: undefined });
                return;
              }
              const next = Math.round(Math.max(0, Number(raw)) * 100);
              onChange({ priceMax: Number.isFinite(next) ? next : undefined });
            }}
            className="w-full border-slate-300 bg-white text-slate-900"
            placeholder={`${maxPlaceholder}`}
          />
        </div>
        <p className="text-xs text-slate-600">
          {t("discovery.filterSidebar.active", { label: priceLabel })}
        </p>
      </div>
    </aside>
  );
}
